import React from 'react';
import { useEcho } from '@laravel/echo-react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { enqueueSnackbar } from 'notistack';
import { useLocation, useNavigate } from 'react-router';
import { getCurrentMember } from '@api/memberApi';
import { CurrentUser } from '@api/memberApi/interfaces';
import { useAuth } from '@context/AuthProvider';
import { useBroadcasting } from '@hooks/useBroadcasting';
import {
  canAccessManagePath,
  permissionsSignature,
} from '@auth/manageRouteAccess';
import { CURRENT_USER_QUERY_KEY } from '@reactQuery/auth';
import {
  IPermissionsChannelListener,
  IPermissionsLiveUpdates,
} from './interfaces';

const PermissionsChannelListener: React.FC<IPermissionsChannelListener> = ({
  userId,
}): null => {
  const { channel } = useBroadcasting();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  useEcho(
    channel(`user.${userId}.permissions`),
    '.permissions.updated',
    async () => {
      const previousUser = queryClient.getQueryData<CurrentUser>(
        CURRENT_USER_QUERY_KEY
      );

      const updatedUser = await queryClient.fetchQuery({
        queryKey: CURRENT_USER_QUERY_KEY,
        queryFn: getCurrentMember,
      });

      if (!updatedUser || !previousUser) {
        return;
      }

      if (permissionsSignature(previousUser) === permissionsSignature(updatedUser)) {
        return;
      }

      enqueueSnackbar(t('authorization:portal.permissions.updated'), {
        variant: 'info',
      });

      if (!canAccessManagePath(location.pathname, updatedUser)) {
        enqueueSnackbar(t('authorization:portal.permissions.redirected'), {
          variant: 'warning',
        });
        navigate('/', { replace: true });
      }
    }
  );

  return null;
};

export const PermissionsLiveUpdates: React.FC<
  IPermissionsLiveUpdates
> = (): JSX.Element | null => {
  const { currentUser } = useAuth();

  if (!currentUser?.id || !currentUser.broadcasting) {
    return null;
  }

  return <PermissionsChannelListener userId={currentUser.id} />;
};
